import { useCallback, useState } from "react";
import { useStaffOperations } from "./useStaffOperations";
import { Staff, StaffFormData } from "src/shared/services/interfacev2";

export const useStaffHandlers = (staffData: Staff[], refetch: () => void) => {
  const { addStaff, updateStaff, deleteStaff } = useStaffOperations(refetch);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [deleteStaffName, setDeleteStaffName] = useState("");

  const handleAddStaff = useCallback(
    async (formData: StaffFormData): Promise<boolean> => {
      const success = await addStaff(formData); // true / false

      if (success) {
        setAddDialogOpen(false);
      }

      return success; // IMPORTANT
    },
    [addStaff],
  );

  // ---------------------------------------
  // UPDATE STAFF - MUST return boolean
  // ---------------------------------------
  const handleUpdateStaff = useCallback(
    async (id: string, formData: StaffFormData): Promise<boolean> => {
      const success = await updateStaff(id, formData);

      if (success) {
        setEditDialogOpen(false);
        setSelectedStaff(null);
      }

      return success;
    },
    [updateStaff],
  );

  // ---------------------------------------
  // DELETE STAFF - MUST return boolean
  // ---------------------------------------
  const handleDeleteStaff = useCallback(
    async (id: string): Promise<boolean> => {
      const success = await deleteStaff(id);

      if (success) {
        setDeleteDialogOpen(false);
        setSelectedStaff(null);
      }

      return success;
    },
    [deleteStaff],
  );

  // ---------------------------------------
  // EDIT CLICK
  // ---------------------------------------
  const handleEditClick = useCallback((staff: Staff) => {
    setSelectedStaff(staff);
    setEditDialogOpen(true);
  }, []);

  // ---------------------------------------
  // DELETE CLICK
  // ---------------------------------------
  const handleDeleteClick = useCallback(
    (id: string, name: string) => {
      const staffToDelete = staffData.find((staff) => staff._id.$oid === id);

      if (staffToDelete) {
        setSelectedStaff(staffToDelete);
        setDeleteStaffName(name);
        setDeleteDialogOpen(true);
      }
    },
    [staffData],
  );

  return {
    // STATE
    addDialogOpen,
    editDialogOpen,
    deleteDialogOpen,
    selectedStaff,
    deleteStaffName,

    // HANDLERS
    handleAddStaff,
    handleUpdateStaff,
    handleDeleteStaff,
    handleEditClick,
    handleDeleteClick,

    // SETTERS
    setAddDialogOpen,
    setEditDialogOpen,
    setDeleteDialogOpen,
    setSelectedStaff,
  };
};
